import React, { useEffect, useState } from "react";
import {
  Box, Button, CircularProgress, MenuItem, Paper, Select, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Typography, Chip, Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { AccountingService } from "services/accounting";

const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const fmtCLP = (n) => (n == null || isNaN(n) ? "—" : "$" + Math.round(n).toLocaleString("es-CL"));

// ─── Selector de período común ─────────────────────────────────────
function PeriodSelector({ period, onChange }) {
  const now = new Date();
  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];
  return (
    <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
      <Select size="small" value={period.month} onChange={(e) => onChange({ ...period, month: e.target.value })}>
        {MONTHS.map((m, i) => <MenuItem key={m} value={i + 1}>{m}</MenuItem>)}
      </Select>
      <Select size="small" value={period.year} onChange={(e) => onChange({ ...period, year: e.target.value })}>
        {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
      </Select>
    </Box>
  );
}

function DownloadButtons({ memberId, kind, period }) {
  const [busy, setBusy] = useState(null);
  const download = async (ext) => {
    setBusy(ext);
    try {
      await AccountingService.downloadReport(memberId, kind, ext, period);
    } catch (e) {
      console.error("download failed", e);
    } finally {
      setBusy(null);
    }
  };
  // PDF solo para income-statement, balance-sheet, balance-8-col
  const supportsPdf = ["income-statement", "balance-sheet", "balance-8-col"].includes(kind);
  return (
    <Box sx={{ display: "flex", gap: 1 }}>
      {supportsPdf && (
        <Button
          size="small"
          variant="outlined"
          startIcon={busy === "pdf" ? <CircularProgress size={14} /> : <DownloadIcon />}
          onClick={() => download("pdf")}
          disabled={busy != null}
        >
          PDF
        </Button>
      )}
      <Button
        size="small"
        variant="outlined"
        startIcon={busy === "xlsx" ? <CircularProgress size={14} /> : <DownloadIcon />}
        onClick={() => download("xlsx")}
        disabled={busy != null}
      >
        Excel
      </Button>
    </Box>
  );
}

// ─── Estado de Resultados ─────────────────────────────────────────
export function IncomeStatementPanel({ memberId }) {
  const [period, setPeriod] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    AccountingService.getIncomeStatement(memberId, period)
      .then((r) => setData(r.report))
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [memberId, period.month, period.year]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
        <PeriodSelector period={period} onChange={setPeriod} />
        <DownloadButtons memberId={memberId} kind="income-statement" period={period} />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
      ) : !data ? null : (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Estado de Resultados — {MONTHS[period.month - 1]} {period.year}</Typography>
          <TableContainer>
            <Table size="small">
              <TableBody>
                <Row label="Ingresos por ventas" value={data.revenue?.total} bold />
                <Row label="Costo de ventas" value={-Math.abs(data.costOfSales?.total || 0)} />
                <Row label="Utilidad bruta" value={data.grossProfit} bold accent="#10b981" />
                <Row label="Gastos operacionales" value={-Math.abs(data.operatingExpenses?.total || 0)} />
                <Row label="Resultado operacional" value={data.operatingIncome} bold accent="#3b82f6" />
                <Row label="Otros ingresos" value={data.otherIncome?.total || 0} />
                <Row label="Otros gastos" value={-Math.abs(data.otherExpenses?.total || 0)} />
                <Row
                  label="RESULTADO NETO"
                  value={data.netIncome}
                  bold
                  big
                  accent={data.netIncome >= 0 ? "#10b981" : "#ef4444"}
                />
              </TableBody>
            </Table>
          </TableContainer>

          {data.operatingExpenses?.accounts?.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Detalle gastos operacionales</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Cuenta</TableCell><TableCell align="right">Monto</TableCell></TableRow></TableHead>
                  <TableBody>
                    {data.operatingExpenses.accounts.map((a) => (
                      <TableRow key={a.accountCode}>
                        <TableCell><code>{a.accountCode}</code> {a.accountName}</TableCell>
                        <TableCell align="right">{fmtCLP(a.balance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
}

function Row({ label, value, bold, big, accent }) {
  return (
    <TableRow>
      <TableCell sx={{ fontWeight: bold ? 700 : 400, fontSize: big ? 16 : 14, py: big ? 1.5 : 1 }}>
        {label}
      </TableCell>
      <TableCell align="right" sx={{ fontWeight: bold ? 700 : 400, fontSize: big ? 18 : 14, color: accent }}>
        {fmtCLP(value)}
      </TableCell>
    </TableRow>
  );
}

// ─── Balance General ──────────────────────────────────────────────
export function BalanceSheetPanel({ memberId }) {
  const [period, setPeriod] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    AccountingService.getBalanceSheet(memberId, period)
      .then((r) => setData(r.report))
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [memberId, period.month, period.year]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
        <PeriodSelector period={period} onChange={setPeriod} />
        <DownloadButtons memberId={memberId} kind="balance-sheet" period={period} />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
      ) : !data ? null : (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Activos</Typography>
            <Table size="small">
              <TableBody>
                <Row label="Activo corriente" value={data.assets?.current} />
                <Row label="Activo fijo" value={data.assets?.fixed} />
                <Row label="Otros activos" value={data.assets?.other} />
                <Row label="Total Activos" value={data.assets?.total} bold accent="#3b82f6" />
              </TableBody>
            </Table>
            {data.assets?.accounts?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">Detalle:</Typography>
                {data.assets.accounts.map((a) => (
                  <Box key={a.accountCode} sx={{ display: "flex", justifyContent: "space-between", fontSize: 12, py: 0.3 }}>
                    <span><code>{a.accountCode}</code> {a.accountName}</span>
                    <span>{fmtCLP(a.balance)}</span>
                  </Box>
                ))}
              </Box>
            )}
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Pasivos y Patrimonio</Typography>
            <Table size="small">
              <TableBody>
                <Row label="Pasivo corriente" value={data.liabilities?.current} />
                <Row label="Pasivo largo plazo" value={data.liabilities?.longTerm} />
                <Row label="Total Pasivos" value={data.liabilities?.total} bold accent="#f59e0b" />
                <Row label="Capital y reservas" value={data.equity?.capitalAndReserves} />
                <Row label="Resultado del período" value={data.equity?.resultOfPeriod} accent={data.equity?.resultOfPeriod >= 0 ? "#10b981" : "#ef4444"} />
                <Row label="Total Patrimonio" value={data.equity?.total} bold accent="#3b82f6" />
                <Row label="Total Pasivo + Patrimonio" value={(data.liabilities?.total || 0) + (data.equity?.total || 0)} bold />
              </TableBody>
            </Table>
            <Box sx={{ mt: 2 }}>
              <Chip size="small" label={data.isBalanced ? "Balance cuadrado ✓" : "DESCUADRADO"} color={data.isBalanced ? "success" : "error"} />
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

// ─── Balance 8 columnas (DJ 1847) ─────────────────────────────────
export function Balance8ColPanel({ memberId }) {
  const [period, setPeriod] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    AccountingService.getBalance8Col(memberId, period)
      .then((r) => setData(r.report))
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [memberId, period.month, period.year]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
        <PeriodSelector period={period} onChange={setPeriod} />
        <DownloadButtons memberId={memberId} kind="balance-8-col" period={period} />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
      ) : !data ? null : (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Balance 8 Columnas — {MONTHS[period.month - 1]} {period.year}</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cuenta</TableCell>
                  <TableCell align="right">Debe</TableCell>
                  <TableCell align="right">Haber</TableCell>
                  <TableCell align="right">S. Deudor</TableCell>
                  <TableCell align="right">S. Acreedor</TableCell>
                  <TableCell align="right">Activo</TableCell>
                  <TableCell align="right">Pasivo</TableCell>
                  <TableCell align="right">Pérdida</TableCell>
                  <TableCell align="right">Ganancia</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.rows || []).map((r) => (
                  <TableRow key={r.accountCode}>
                    <TableCell><code>{r.accountCode}</code> {r.accountName}</TableCell>
                    <TableCell align="right">{fmtCLP(r.debit)}</TableCell>
                    <TableCell align="right">{fmtCLP(r.credit)}</TableCell>
                    <TableCell align="right">{fmtCLP(r.saldoDeudor)}</TableCell>
                    <TableCell align="right">{fmtCLP(r.saldoAcreedor)}</TableCell>
                    <TableCell align="right">{fmtCLP(r.inventarioActivo)}</TableCell>
                    <TableCell align="right">{fmtCLP(r.inventarioPasivo)}</TableCell>
                    <TableCell align="right">{fmtCLP(r.resultadoPerdida)}</TableCell>
                    <TableCell align="right">{fmtCLP(r.resultadoGanancia)}</TableCell>
                  </TableRow>
                ))}
                {data.totals && (
                  <TableRow sx={{ "& td": { fontWeight: 700 } }}>
                    <TableCell>TOTALES</TableCell>
                    <TableCell align="right">{fmtCLP(data.totals.debit)}</TableCell>
                    <TableCell align="right">{fmtCLP(data.totals.credit)}</TableCell>
                    <TableCell align="right">{fmtCLP(data.totals.saldoDeudor)}</TableCell>
                    <TableCell align="right">{fmtCLP(data.totals.saldoAcreedor)}</TableCell>
                    <TableCell align="right">{fmtCLP(data.totals.inventarioActivo)}</TableCell>
                    <TableCell align="right">{fmtCLP(data.totals.inventarioPasivo)}</TableCell>
                    <TableCell align="right">{fmtCLP(data.totals.resultadoPerdida)}</TableCell>
                    <TableCell align="right">{fmtCLP(data.totals.resultadoGanancia)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}

// ─── F29 completo (engine) ────────────────────────────────────────
export function F29FullPanel({ memberId }) {
  const [period, setPeriod] = useState({ month: new Date().getMonth() + 1, year: new Date().getFullYear() });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    AccountingService.getF29Full(memberId, period)
      .then((r) => setData(r.report))
      .catch((e) => setError(e?.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  }, [memberId, period.month, period.year]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
        <PeriodSelector period={period} onChange={setPeriod} />
        <DownloadButtons memberId={memberId} kind="f29-full" period={period} />
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
      ) : !data ? null : (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>IVA</Typography>
            <Table size="small">
              <TableBody>
                <Row label="Débito fiscal (cód. 91)" value={data.iva.debitoFiscal} />
                <Row label="Crédito fiscal (cód. 520)" value={data.iva.creditoFiscal} />
                <Row label="Remanente anterior (cód. 504)" value={data.iva.ivaRemanenteAnterior} />
                <Row label="IVA Determinado (cód. 89)" value={data.iva.ivaDeterminado} bold accent="#3b82f6" />
                <Row label="Remanente próximo período (cód. 77)" value={data.iva.ivaRemanente} />
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>PPM</Typography>
            <Table size="small">
              <TableBody>
                <Row label="Base imponible (cód. 563)" value={data.ppm.baseImponible} />
                <Row label="Tasa" value={null} />
                <TableRow><TableCell>Tasa PPM</TableCell><TableCell align="right">{(data.ppm.rate * 100).toFixed(2)}%</TableCell></TableRow>
                <Row label="PPM del período (cód. 48)" value={data.ppm.ppmAmount} bold accent="#3b82f6" />
              </TableBody>
            </Table>
          </Paper>

          <Paper sx={{ p: 3, gridColumn: "1 / 3", background: "#f8fafc" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h6">Total a pagar</Typography>
              <Typography variant="h4" color="error.main" fontWeight={700}>{fmtCLP(data.totalToPay)}</Typography>
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
